import {of as observableOf,  BehaviorSubject, Observable, ReplaySubject, Scheduler, asyncScheduler } from 'rxjs';
import {tap, filter, distinctUntilChanged, observeOn, delay, switchMap, map, takeUntil} from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { animatedButtonDefaultSettings, IAnimatedButtonSettings, ICssClasses } from './animated-button-settings';
import { AnimatedButtonState } from './animated-button-state';
// Fontawesome icons
import { faSpinner, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-component-animated-button',
    templateUrl: './animated-button.component.html',
    styleUrls: ['./animated-button.component.scss']
})
export class AnimatedButtonComponent implements OnInit, OnDestroy {

    public faSpinner = faSpinner;
    public faCheck = faCheck;
    public faTimes = faTimes;

    private static readonly DEFAULT_SUBMITTING_TEXT: string = 'SHARED.COMPONENTS.BUTTON.SAVING';
    private static readonly DEFAULT_SUCCESS_TEXT: string = 'SHARED.COMPONENTS.BUTTON.SAVED';
    // private static readonly DEFAULT_SUBMITTING_TEXT: string = 'SHARED.COMPONENTS.BUTTON.REMOVING';
    // private static readonly DEFAULT_SUCCESS_TEXT: string = 'SHARED.COMPONENTS.BUTTON.REMOVED';
    private static readonly DEFAULT_ERROR_TEXT: string = 'SHARED.COMPONENTS.BUTTON.FAILED';

    public buttonCssClasses: ICssClasses = { };
    public contentCssClasses: ICssClasses = { };

    public outerState$ = new BehaviorSubject<AnimatedButtonState>(AnimatedButtonState.Default);
    public innerState$ = new BehaviorSubject<AnimatedButtonState>(AnimatedButtonState.Default);
    public submittingText$ = new BehaviorSubject<string>(AnimatedButtonComponent.DEFAULT_SUBMITTING_TEXT);
    public successText$ = new BehaviorSubject<string>(AnimatedButtonComponent.DEFAULT_SUCCESS_TEXT);
    public errorText$ = new BehaviorSubject<string>(AnimatedButtonComponent.DEFAULT_ERROR_TEXT);
    public disabled$ = new BehaviorSubject<boolean>(false);

    private destroy$ = new ReplaySubject<boolean>();
    private options$ = new BehaviorSubject<Partial<IAnimatedButtonSettings>>({ });
    private settings$ = new BehaviorSubject<IAnimatedButtonSettings>(Object.assign({ }, animatedButtonDefaultSettings));

    @Input()
    set options(value: Partial<IAnimatedButtonSettings>) { this.options$.next(value); }

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('state')
    set outerState(value: AnimatedButtonState) { this.outerState$.next(value); }

    @Input()
    set submittingText(value: string) { this.submittingText$.next(value); }

    @Input()
    set successText(value: string) { this.successText$.next(value); }

    @Input()
    set errorText(value: string) { this.errorText$.next(value); }

    @Input()
    set disabled(value: boolean) { this.disabled$.next(value); }
    get disabled(): boolean {
        let disabled = false;
        if (this.settings.enabledOnCompleted) {
            disabled = [AnimatedButtonState.Submitting].indexOf(this.innerState) > -1;
        } else {
            disabled = [AnimatedButtonState.Submitting, AnimatedButtonState.Success].indexOf(this.innerState) > -1;
        }

        return this.disabled$.value || disabled;
    }

    @Output()
    public stateChanged = new EventEmitter<AnimatedButtonState>();

    @Output()
    public clicked = new EventEmitter<any>();

    get innerState(): AnimatedButtonState { return this.innerState$.value; }
    set innerState(value: AnimatedButtonState) { this.innerState$.next(value); }

    get settings(): IAnimatedButtonSettings { return this.settings$.value; }
    set settings(value: IAnimatedButtonSettings) { this.settings$.next(value); }

    public ngOnInit() {
        this.options$.pipe(
            takeUntil(this.destroy$),
            map(options => this.settings = { ...animatedButtonDefaultSettings, ...options }),)
            .subscribe(() => this.applySettings());

        this.outerState$.pipe(
            takeUntil(this.destroy$),
            switchMap(outer => {
                switch (outer) {
                    case AnimatedButtonState.Success:
                    case AnimatedButtonState.Error:
                        return observableOf(outer).pipe(
                            delay(this.settings.submittingTimeOut));

                    default:
                        return observableOf(outer);
                }
            }),)
            .subscribe(outer => this.innerState = outer);

        this.innerState$.pipe(
            takeUntil(this.destroy$),
            observeOn(asyncScheduler),
            distinctUntilChanged(),
            filter(inner => !!inner),
            switchMap(inner => {
                switch (inner) {
                    case AnimatedButtonState.Success:
                    case AnimatedButtonState.Error:
                        return observableOf(inner).pipe(
                            tap(current => this.stateChanged.emit(current)),
                            tap(current => this.applyStyle(current)),
                            delay(this.settings.completedTimeOut),
                            tap(() => {
                                if (this.settings.returnToDefaultState) {
                                    this.innerState = AnimatedButtonState.Default;
                                }
                            }),);

                    default:
                        return observableOf(inner).pipe(
                            tap(current => this.applyStyle(current)));
                }
            }),)
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public onButtonClick(event) {
        event.stopPropagation();
        if (!this.disabled) {
            this.clicked.emit(event);
        }
    }

    private applySettings() {
        this.buttonCssClasses['sa-btn-animated'] = true;

        this.buttonCssClasses['btn'] = true;
        this.buttonCssClasses[this.settings.defaultClass] = true;
        this.buttonCssClasses[this.settings.submittingClass] = false;
        this.buttonCssClasses[this.settings.successClass] = false;
        this.buttonCssClasses[this.settings.errorClass] = false;

        this.contentCssClasses['flex-row-reverse'] = this.settings.iconPosition !== 'left';
        this.contentCssClasses['flex-row'] = this.settings.iconPosition === 'left';
    }

    private applyStyle(innerState: AnimatedButtonState) {
        switch (innerState) {
            case AnimatedButtonState.Default:
                this.buttonCssClasses[this.settings.defaultClass] = true;
                this.buttonCssClasses[this.settings.submittingClass] = false;
                this.buttonCssClasses[this.settings.successClass] = false;
                this.buttonCssClasses[this.settings.errorClass] = false;
                break;

            case AnimatedButtonState.Submitting:
                this.buttonCssClasses[this.settings.defaultClass] = false;
                this.buttonCssClasses[this.settings.submittingClass] = true;
                this.buttonCssClasses[this.settings.successClass] = false;
                this.buttonCssClasses[this.settings.errorClass] = false;
                break;

            case AnimatedButtonState.Success:
                this.buttonCssClasses[this.settings.defaultClass] = false;
                this.buttonCssClasses[this.settings.submittingClass] = false;
                this.buttonCssClasses[this.settings.successClass] = true;
                this.buttonCssClasses[this.settings.errorClass] = false;
                break;

            case AnimatedButtonState.Error:
                this.buttonCssClasses[this.settings.defaultClass] = false;
                this.buttonCssClasses[this.settings.submittingClass] = false;
                this.buttonCssClasses[this.settings.successClass] = false;
                this.buttonCssClasses[this.settings.errorClass] = true;
                break;
        }

        this.stateChanged.emit(innerState);
    }
}
